import { useMemo, useState } from "react"
import { v4 as uuidv4 } from 'uuid'
// components
import { Card, Col } from 'react-bootstrap'
import { TextInput, TextArea, Button } from 'carbon-components-react';
import ConfirmModal from "../../../../components/Modal/ConfirmModal"
// styles
import styled from 'styled-components';

const FieldGroupArray = ({
  id, // field group ID
  fieldMap,
  sourceData,
  submitData,
  setDataToSubmit,
  isDirty,
  label,
  readOnly = false,
  // styles
  isCol = false,
  colProps,
  className,
}) => {

  const displayData = useMemo(() => {
    if (submitData) return submitData;
    if (sourceData?.length > 0) return [...sourceData]
    return null
  }, [submitData, sourceData])

  // delete item
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)


  /** EVENT HANDLERS */

  const onCreateGroup = () => {
    setDataToSubmit({ type: 'CREATE_GROUP', name: id, fieldMap })
  }

  const handleBlur = (evt, itemId) => {
    const fieldId = evt.target.id
    const value = evt.target.value
    setDataToSubmit({
      type: 'SET_FIELD',
      name: id,
      id: itemId,
      field: fieldId,
      value,
    })
  }

  const onMoveUp = (itemId) => {
    if (itemId !== 1) {
      setDataToSubmit({
        type: 'MOVE_UP',
        name: id,
        order_id: itemId,
      })
    }
  }

  const onMoveDown = (itemId) => {
    if (itemId !== displayData?.length) {
      setDataToSubmit({
        type: 'MOVE_DOWN',
        name: id,
        order_id: itemId,
      })
    }
  }

  const handleDelete = (item) => {
    setItemToDelete(item)
    setOpenDeleteModal(true)
  }

  const confirmDeletion = () => {
    setDataToSubmit({
      type: 'DELETE_ITEM',
      name: id,
      id: itemToDelete.id,
    })
    setOpenDeleteModal(false)
    setItemToDelete(null)
  }

  /** RENDERERS */

  const btnStyle = {
    padding: "5px 10px",
    height: "30px",
    minHeight: "30px",
    width: "fit-content",
  }

  const groupNumberTag = (number) => {
    return <GroupNumTagStyled>
      {number}
    </GroupNumTagStyled>
  }

  const content = useMemo(() => {
    return <>
      {displayData && displayData?.map((item) => {
        return <CardStyled
          isDirty={isDirty}
          key={item.id ? item.id : uuidv4()}
        >
          <Card className="mb-2">
            <Card.Body>
              {fieldMap?.map((field) => {
                const defaultValue = item?.[field.id]
                const props = {
                  id: `${field.id}`,
                  key: `${field.id}`,
                  labelText: field.labelText,
                  defaultValue,
                  className: "mb-3",
                }
                const readOnlyProps = readOnly ? {
                  readOnly: true,
                  onFocus: (evt) => evt.target.select(),
                } : {
                  onBlur: (evt) => handleBlur(evt, item.id),
                };
                if (field.type === 'textInput') {
                  return <TextInput
                    {...props}
                    {...readOnlyProps}
                    size="sm"
                  />
                } else if (field.type === 'textArea') {
                  return <TextArea
                    {...props}
                    {...readOnlyProps}
                    rows={field.rows}
                  />
                }
              })}
            </Card.Body>
            {!readOnly && <Card.Footer>
              <GroupFooterLeftStyled>
                {groupNumberTag(item.order_id)}
                <span>
                  <GroupMoveIconStyled
                    className="fas fa-angle-up"
                    disabled={item.order_id === 1 ? true : false}
                    onClick={() => onMoveUp(item.order_id)}
                  />
                  <GroupMoveIconStyled
                    className="fas fa-angle-down"
                    disabled={item.order_id === displayData?.length ? true : false}
                    onClick={() => onMoveDown(item.order_id)}
                  />
                </span>
              </GroupFooterLeftStyled>
              <TrashIconStyled
                className="fal fa-trash-alt"
                onClick={() => handleDelete(item)}
              />
            </Card.Footer>}
          </Card>
        </CardStyled>
      })}
      {isDirty &&
        (!displayData || displayData.length === 0) &&
        <WarningTextStyled>Save data to permanently delete</WarningTextStyled>
      }
      {!readOnly && <AddCardBtnStyled>
        <Button
          kind="primary"
          style={btnStyle}
          onClick={onCreateGroup}
        >+ Add {label ? label : 'Group'}</Button>
      </AddCardBtnStyled>}
      <ConfirmModal
        title={`Delete ${label} item`}
        content={`Are you sure you want to delete ${label} item #${itemToDelete?.order_id}?`}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={confirmDeletion}
      />
    </>
  }, [displayData, openDeleteModal])

  if (isCol || colProps) {
    return <Col
      className={className}
      {...colProps}
    >{content}</Col>
  } else {
    return <div className={className}>{content}</div>
  }
}

const CardStyled = styled.div`
  .card {
    background-color: ${({ isDirty }) => isDirty ? '#e6f7ff' : 'inherit'};

    .bx--text-input, .bx--text-area {
      background-color: ${({ isDirty }) => isDirty ? 'white' : 'inherit'};
    }

    .card-footer {
      background: #f5f5f5; // #fafafa;
    }
  }
`

const GroupNumTagStyled = styled.span`
  font-size: 12px;
  color: black;
  background-color: #d9d9d9;
  border-radius: 10px;
  padding: 3px 6px;
`

const GroupFooterLeftStyled = styled.span`
  display: flex;
  align-items: center;
`

const IconStyled = styled.i`
  cursor: pointer;
`

const GroupMoveIconStyled = styled(IconStyled)`
  font-size: 16px;
  margin-left: 8px;
  display: block;
  color: ${({ disabled }) => disabled ? '#d9d9d9' : '#8c8c8c'};
  cursor: ${({ disabled }) => disabled ? 'inherit' : 'pointer'};

  &:hover {
    color: ${({ disabled }) => disabled ? '#d9d9d9' : '#0f62fe'};
  }

  &.fa-angle-up {
    padding-top: 2px;
  }

  &.fa-angle-down {
    padding-bottom: 2px;
  }
`
const TrashIconStyled = styled(IconStyled)`
  margin-left: 12px;
  color: #434343;

  &:hover {
    color: red;
  }
`

const WarningTextStyled = styled.div`
  font-size: 14px;
  margin-bottom: 1em;
  color: #f5222d;
`

const AddCardBtnStyled = styled.div`
  display: flex;
  justify-content: flex-start;
`

export default FieldGroupArray